document.addEventListener("DOMContentLoaded", () => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mobileQuery = window.matchMedia("(max-width: 768px)");

  const preferredValue = (video, name, allowFallback) => {
    const mobile = mobileQuery.matches;
    const mobileValue = video.getAttribute("data-" + name + "-mb");
    const desktopValue = video.getAttribute("data-" + name + "-pc");
    if (allowFallback === false) {
      return mobile ? mobileValue : desktopValue;
    }
    return mobile ? mobileValue || desktopValue : desktopValue || mobileValue;
  };

  const prepareInlinePlayback = (video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "true");
    video.setAttribute("x5-playsinline", "");
    video.setAttribute("x5-video-player-type", "h5");
    video.setAttribute("x5-video-player-fullscreen", "false");
  };

  const applyVideoSource = (video, loadSource) => {
    const poster = preferredValue(video, "poster");
    const src = preferredValue(video, "src");
    const mobile = mobileQuery.matches;

    if (poster) video.poster = poster;
    if (mobile) {
      const ratio = preferredValue(video, "ratio", false);
      const width = preferredValue(video, "width", false);
      const height = preferredValue(video, "height", false);
      if (ratio) video.style.aspectRatio = ratio;
      if (width && height) {
        video.setAttribute("width", width);
        video.setAttribute("height", height);
      }
    } else {
      const ratio = video.getAttribute("data-ratio-pc");
      const width = video.getAttribute("data-width-pc");
      const height = video.getAttribute("data-height-pc");
      if (ratio) {
        video.style.aspectRatio = ratio;
      } else {
        video.style.aspectRatio = "";
      }
      if (width && height) {
        video.setAttribute("width", width);
        video.setAttribute("height", height);
      } else {
        video.removeAttribute("width");
        video.removeAttribute("height");
      }
    }
    if (loadSource === false || !src) return false;

    const currentSrc = video.getAttribute("src");
    if (currentSrc === src || video.currentSrc === src || video.src === src) return false;

    video.src = src;
    return true;
  };

  const bindVideoLoop = (video) => {
    if (video.dataset.loopBound === "true") return;
    video.dataset.loopBound = "true";
    video.addEventListener("ended", () => {
      if (video.dataset.shouldPlay !== "true") return;
      try {
        video.currentTime = 0;
      } catch (error) {
        return;
      }
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    });
  };

  const playVideo = (video) => {
    prepareInlinePlayback(video);
    bindVideoLoop(video);
    video.dataset.shouldPlay = "true";
    video.setAttribute("autoplay", "");
    video.preload = "auto";

    const sourceChanged = applyVideoSource(video);
    if (!video.getAttribute("src")) return;
    if (!video.paused && !video.ended && !sourceChanged) return;

    const start = () => {
      video.dataset.playPending = "false";
      if (video.dataset.shouldPlay !== "true") return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            delete video.dataset.needsGesture;
          })
          .catch(() => {
            video.dataset.needsGesture = "true";
          });
      }
    };

    if (!sourceChanged && video.readyState >= 2) {
      start();
      return;
    }

    if (video.dataset.playPending === "true" && !sourceChanged) return;
    video.dataset.playPending = "true";
    if (video._onReady) {
      video.removeEventListener("canplay", video._onReady);
      video.removeEventListener("loadeddata", video._onReady);
    }
    const onReady = () => {
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("loadeddata", onReady);
      start();
    };
    video._onReady = onReady;
    video.addEventListener("canplay", onReady);
    video.addEventListener("loadeddata", onReady);
    if (sourceChanged || video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
      video.load();
    }
  };

  const pauseVideo = (video) => {
    video.dataset.shouldPlay = "false";
    video.dataset.playPending = "false";
    video.removeAttribute("autoplay");
    video.pause();
  };

  const playSlideVideos = (slideshowEl, activeSlide) => {
    if (!slideshowEl) return;

    slideshowEl.querySelectorAll("video.slideshow-banner-video").forEach((video) => {
      const slide = video.closest(".slideshow-item");
      const isActive = !activeSlide || slide === activeSlide;
      prepareInlinePlayback(video);

      if (isActive && !reducedMotion) {
        playVideo(video);
      } else if (isActive) {
        applyVideoSource(video, false);
        pauseVideo(video);
      } else {
        pauseVideo(video);
      }
    });
  };

  const unlockGesturePlayback = () => {
    document.querySelectorAll("video.slideshow-banner-video").forEach((video) => {
      if (video.dataset.shouldPlay !== "true" || video.dataset.needsGesture !== "true") return;
      prepareInlinePlayback(video);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            delete video.dataset.needsGesture;
          })
          .catch(() => {});
      }
    });
  };

  document.addEventListener("touchstart", unlockGesturePlayback, { passive: true });
  document.addEventListener("pointerdown", unlockGesturePlayback);

  const slideshows = document.querySelectorAll("slideshow-swiper.home-slideshow");

  slideshows.forEach((slideshowEl) => {
    const slides = slideshowEl.querySelectorAll(".swiper-slide");
    if (!slides.length) return;

    const syncActive = () => {
      const activeSlide =
        slideshowEl.querySelector(".swiper-slide-active") || slides[0];
      playSlideVideos(slideshowEl, activeSlide);
    };

    if (slides.length < 2) {
      syncActive();
      if (typeof mobileQuery.addEventListener === "function") {
        mobileQuery.addEventListener("change", syncActive);
      }
      return;
    }

    const autoplay = slideshowEl.getAttribute("data-autoplay");
    const speed = slideshowEl.getAttribute("data-speed");

    const swiperConfig = {
      autoHeight: true,
      navigation: {
        nextEl: slideshowEl.querySelector(".home-slideshow-btn-next"),
        prevEl: slideshowEl.querySelector(".home-slideshow-btn-prev"),
      },
      pagination: {
        el: slideshowEl.querySelector(".home-slideshow-pagination"),
        clickable: true,
      },
    };

    if (autoplay === "true") {
      swiperConfig.autoplay = {
        delay: Number(speed) || 7000,
        disableOnInteraction: false,
      };
    }

    const swiper = new Swiper(slideshowEl, swiperConfig);

    const updateHeight = () => {
      swiper.updateAutoHeight(0);
    };

    slideshowEl.querySelectorAll("video.slideshow-banner-video").forEach((video) => {
      video.addEventListener("loadedmetadata", updateHeight);
      video.addEventListener("loadeddata", updateHeight);
    });

    const syncAndResize = () => {
      syncActive();
      updateHeight();
    };

    swiper.on("slideChange", syncAndResize);
    swiper.on("resize", syncAndResize);
    syncActive();
    updateHeight();

    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", syncAndResize);
    }

    const autoplayButton = slideshowEl.querySelector(".home-slideshow-autoplay");
    if (!autoplayButton || autoplay !== "true") return;

    const pauseLabel =
      window.accessibilityStrings?.pauseSlideshow || "Pause slideshow";
    const playLabel =
      window.accessibilityStrings?.playSlideshow || "Play slideshow";

    autoplayButton.addEventListener("click", () => {
      if (swiper.autoplay.running) {
        swiper.autoplay.stop();
        autoplayButton.classList.add("home-slideshow-autoplay--paused");
        autoplayButton.setAttribute("aria-label", playLabel);
      } else {
        swiper.autoplay.start();
        autoplayButton.classList.remove("home-slideshow-autoplay--paused");
        autoplayButton.setAttribute("aria-label", pauseLabel);
      }
    });
  });

  document.addEventListener("visibilitychange", () => {
    slideshows.forEach((slideshowEl) => {
      if (document.hidden) {
        slideshowEl.querySelectorAll("video.slideshow-banner-video").forEach((video) => {
          pauseVideo(video);
        });
        return;
      }

      const activeSlide =
        slideshowEl.querySelector(".swiper-slide-active") ||
        slideshowEl.querySelector(".swiper-slide");
      playSlideVideos(slideshowEl, activeSlide);
    });
  });
});
